import { getPostHogClient } from "../posthog-client";
import { recordPreviewTelemetryEvent } from "../telemetry/preview-observer";
import {
  sanitizeTelemetryErrorMessage,
  sanitizeTelemetryErrorType,
  toCorrelationPayload,
  type RenderTelemetryMetadata,
  type StructuredTelemetryError,
} from "../telemetry/render-outcome";
import { GENERATION_START_THRESHOLD_MS } from "./types";
import { logMetric } from "./utils";

type GenerationErrorInput = string | StructuredTelemetryError | undefined;

const normalizeGenerationError = (error: GenerationErrorInput): StructuredTelemetryError => {
  if (typeof error === "string") {
    return { errorMessage: error };
  }

  return error ?? {};
};

/**
 * Tracks the start of an image generation request.
 */
export function trackGenerationStart(metadata: RenderTelemetryMetadata = {}): number {
  const startTime = performance.now();

  logMetric("Generation", {
    status: "STARTED",
    timestamp: new Date().toISOString(),
  });

  const posthog = getPostHogClient();
  const payload = {
    ...toCorrelationPayload(metadata),
  };
  recordPreviewTelemetryEvent("generation_start", payload);
  if (posthog) {
    posthog.capture("generation_start", payload);
  }

  return startTime;
}

/**
 * Tracks when the generation API responds.
 */
export function trackGenerationResponse(
  startTime: number,
  metadata: RenderTelemetryMetadata = {},
): number {
  const responseTime = performance.now();
  const duration = responseTime - startTime;
  const meetsThreshold = duration < GENERATION_START_THRESHOLD_MS;

  logMetric("Generation", {
    status: meetsThreshold ? "✓ RESPONDED (PASS)" : "✗ RESPONDED (FAIL)",
    duration: `${Math.round(duration)}ms`,
    threshold: `${GENERATION_START_THRESHOLD_MS}ms`,
  });

  const posthog = getPostHogClient();
  const payload = {
    duration,
    meetsThreshold,
    threshold: GENERATION_START_THRESHOLD_MS,
    ...toCorrelationPayload(metadata),
  };
  recordPreviewTelemetryEvent("generation_response", payload);
  if (posthog) {
    posthog.capture("generation_response", payload);
  }

  return duration;
}

/**
 * Tracks when generation completes successfully.
 */
export function trackGenerationComplete(
  startTime: number,
  imageCount = 1,
  metadata: RenderTelemetryMetadata = {},
): number {
  const endTime = performance.now();
  const duration = endTime - startTime;

  logMetric("Generation", {
    status: "COMPLETED",
    duration: `${Math.round(duration)}ms`,
    imageCount,
  });

  const posthog = getPostHogClient();
  const payload = {
    duration,
    imageCount,
    ...toCorrelationPayload(metadata),
  };
  recordPreviewTelemetryEvent("generation_complete", payload);
  if (posthog) {
    posthog.capture("generation_complete", payload);
  }

  return duration;
}

/**
 * Tracks when generation fails.
 */
export function trackGenerationError(
  startTime: number,
  error: GenerationErrorInput,
  metadata: RenderTelemetryMetadata = {},
): number {
  const endTime = performance.now();
  const duration = endTime - startTime;
  const normalizedError = normalizeGenerationError(error);
  const errorType = sanitizeTelemetryErrorType(normalizedError.errorType);
  const errorMessage = sanitizeTelemetryErrorMessage(normalizedError.errorMessage);

  logMetric("Generation", {
    status: "FAILED",
    duration: `${Math.round(duration)}ms`,
    error: errorMessage || errorType || "Unknown error",
  });

  const posthog = getPostHogClient();
  const payload = {
    duration,
    error: errorMessage || errorType || "Unknown error",
    ...(errorType ? { error_type: errorType } : {}),
    ...(errorMessage ? { error_message: errorMessage } : {}),
    ...toCorrelationPayload(metadata),
  };
  recordPreviewTelemetryEvent("generation_error", payload);
  if (posthog) {
    posthog.capture("generation_error", payload);
  }

  return duration;
}
